/**
 * RoleWizardDialog — create/edit a Rolle or Berechtigungsgruppe (Story UI-U6 AC4).
 *
 * name/description · count row "n von N Rechten ausgewählt" · Schnellauswahl
 * Alle / Keine / Standard (Standard = the catalog's `basic` role selection, server
 * truth — NOT re-derived client-side) · permission catalog as category cards with
 * checkbox rows + mono key · system roles read-only for name/Funktionen (wildcard =
 * everything checked, zero editing) · save = POST /admin/roles | PATCH + PUT permissions ·
 * delete = DELETE (ghost-destructive) · a "Sichtbarkeit" deny section (only with
 * visibility.read; editable only with visibility.manage). Kap. 5.8: the admin wildcard role
 * CAN now carry Sichtbarkeit/Feldrechte denies ("Admin-Sein allein genügt nie"), so those
 * sections are reachable for it too — only an Administrationsrolle (ist_administrationsrolle)
 * is barred from customer-data denies.
 *
 * Feldrechte polarity: a regular catalog field defaults to visible + editable (Minus-Basis,
 * the row only ever RESTRICTS). A field the catalog flags `opt_in` (besondere Personendaten,
 * N3 secure-by-default) is INVERTED: no stored rule = NOT granted, rendered OFF with a marker,
 * and sent as (visible=false, editable=false), which the api drops as that field's default.
 * Only an explicit toggle by the admin sends visible=true (+ editable=true) = the GRANT. A
 * stored grant renders ON and is re-sent unchanged.
 *
 * api error envelope surfaced on 403/409/400.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge } from '../../components/ui/Badge';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { Button, Dialog, TextInput } from './components';
import { useEffectivePermissions } from './useEffectivePermissions';
import {
  useRoleVisibilityDenies,
  useVisibilityCatalog,
  useFieldPermissionCatalog,
  useRoleFieldPermissions,
} from './hooks';
import type { FieldPermissionDto, VisibilityKeyDefinition } from '@brokly/shared';
import type { CatalogResponseDto, CatalogRoleEntry } from './types';

/** A field's effective (visible, editable) pair for the Feldrechte section. */
interface FieldRule {
  visible: boolean;
  editable: boolean;
}
const FIELD_DEFAULT: FieldRule = { visible: true, editable: true };
/** The inverted default of an `opt_in` catalog field: no stored rule = NOT granted. */
const FIELD_OPT_IN_DEFAULT: FieldRule = { visible: false, editable: false };
const fieldKeyOf = (entity: string, field: string): string => `${entity}::${field}`;

export interface WizardRole {
  id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  is_wildcard: boolean;
  /** WS1 §5.8: true = Administrationsrolle (pure parametrization). */
  ist_administrationsrolle: boolean;
  selected_keys: string[];
}

export function RoleWizardDialog({
  mode,
  kind,
  role,
  catalog,
  onClose,
}: {
  mode: 'create' | 'edit';
  kind: 'rolle' | 'gruppe';
  role?: WizardRole;
  catalog: CatalogResponseDto;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { can } = useEffectivePermissions();
  const readOnly = mode === 'edit' && (role?.is_system ?? false);
  const isWildcard = role?.is_wildcard ?? false;

  const allKeys = useMemo(
    () => catalog.categories.flatMap((c) => c.permissions.map((p) => p.key)),
    [catalog],
  );
  const total = allKeys.length;
  /** Standard = the seeded `basic` role's selection (server truth). */
  const standardKeys = useMemo<string[]>(() => {
    const basic = catalog.roles.find((r) => r.is_system && !r.is_wildcard);
    return basic?.selected_keys ?? [];
  }, [catalog]);

  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [istAdmin, setIstAdmin] = useState(role?.ist_administrationsrolle ?? false);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(isWildcard ? allKeys : (role?.selected_keys ?? [])),
  );
  const [error, setError] = useState<string | null>(null);

  // An Administrationsrolle (§5.8) holds ONLY parametrization keys — it may NOT carry
  // visibility denies or field rights, so those config sections are hidden when it is on.
  // The admin WILDCARD role is a different case: per Kap. 5.8 it CAN now hold denies
  // ("Admin-Sein allein genügt nie"), so !isWildcard is intentionally NOT gated here.
  // ── Sichtbarkeit (deny-set) — edit mode + visibility.read only ──────────────
  const canSeeVisibility = mode === 'edit' && can('visibility.read') && !istAdmin;
  const canManageVisibility = can('visibility.manage');
  const visCatalog = useVisibilityCatalog(canSeeVisibility);
  const roleDenies = useRoleVisibilityDenies(role?.id ?? '', canSeeVisibility && Boolean(role));
  const [denies, setDenies] = useState<Set<string> | null>(null);
  const effectiveDenies = denies ?? new Set(roleDenies.data?.denied_keys ?? []);

  // Bereich 3: group the visibility catalog into a parent → children tree so cards
  // sit UNDER their tab (Menüpunkt → Tab → Kachel). Every node stays its own deny
  // toggle; denying a tab already cascades to its cards at enforcement time.
  const visByParent = useMemo(() => {
    const m = new Map<string | null, VisibilityKeyDefinition[]>();
    for (const v of visCatalog.data?.catalog ?? []) {
      const arr = m.get(v.parent) ?? [];
      arr.push(v);
      m.set(v.parent, arr);
    }
    return m;
  }, [visCatalog.data]);

  const toggleDeny = (key: string) => {
    setDenies(() => {
      const base = denies ?? new Set(roleDenies.data?.denied_keys ?? []);
      const next = new Set(base);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Recursive row renderer — indents each level so a card reads as nested under
  // its tab. `effectiveDenies.has` is EXACT (this key explicitly in the deny set);
  // an ancestor deny is applied at enforcement time, not shown as a child checkbox.
  const renderVisNodes = (parent: string | null, depth: number): ReactNode =>
    (visByParent.get(parent) ?? []).map((v) => {
      const denied = effectiveDenies.has(v.key);
      const children = visByParent.get(v.key) ?? [];
      return (
        <div key={v.key}>
          <label
            className="flex items-start gap-3 py-2 pr-3"
            style={{ paddingLeft: `${12 + depth * 18}px` }}
          >
            <input
              type="checkbox"
              aria-label={`${v.label} ausblenden`}
              checked={denied}
              disabled={!canManageVisibility}
              onChange={() => toggleDeny(v.key)}
              className="mt-1 size-4 shrink-0 rounded"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span
                  className={`text-sm ${v.level === 'kachel' ? 'font-medium' : 'font-semibold'}`}
                >
                  {v.label}
                </span>
                {v.sensitive && <Badge variant="warning">sensibel</Badge>}
              </div>
              <div className="text-xs text-muted-foreground">{v.description}</div>
            </div>
          </label>
          {children.length > 0 && (
            <div className="ml-3 border-l border-border/60">{renderVisNodes(v.key, depth + 1)}</div>
          )}
        </div>
      );
    });

  // ── Feldrechte (WS1 §5.2) — edit mode + visibility.read only ────────────────
  // Like Sichtbarkeit above: the wildcard role CAN carry field rules (Kap. 5.8); only an
  // Administrationsrolle is barred, so we gate on !istAdmin, not !isWildcard.
  const canSeeFields = mode === 'edit' && can('visibility.read') && !istAdmin;
  const fieldCatalog = useFieldPermissionCatalog(canSeeFields);
  const roleFields = useRoleFieldPermissions(role?.id ?? '', canSeeFields && Boolean(role));
  // The catalog's `opt_in` flag is server truth: which fields are default-denied is decided
  // by the shared catalog, the wizard only renders the inverted polarity it is told about.
  const optInKeys = useMemo(
    () =>
      new Set(
        (fieldCatalog.data?.catalog ?? [])
          .filter((f) => f.opt_in === true)
          .map((f) => fieldKeyOf(f.entity_type, f.field_key)),
      ),
    [fieldCatalog.data],
  );
  const isOptIn = (entity: string, field: string): boolean =>
    optInKeys.has(fieldKeyOf(entity, field));
  // Sparse local edits keyed by `entity::field`; null = untouched (use the fetched rows).
  const [fieldEdits, setFieldEdits] = useState<Map<string, FieldRule> | null>(null);
  const ruleFor = (entity: string, field: string): FieldRule => {
    const key = fieldKeyOf(entity, field);
    if (fieldEdits?.has(key)) return fieldEdits.get(key)!;
    const row = roleFields.data?.field_permissions.find(
      (r) => r.entity_type === entity && r.field_key === field,
    );
    if (row) return { visible: row.visible, editable: row.editable };
    // No stored rule: a regular field is open, an opt-in field is NOT granted (secure-by-default).
    return isOptIn(entity, field) ? FIELD_OPT_IN_DEFAULT : FIELD_DEFAULT;
  };
  const patchFieldRule = (entity: string, field: string, patch: Partial<FieldRule>) => {
    if (!canManageVisibility) return;
    const current = ruleFor(entity, field);
    const next = { ...current, ...patch };
    // A field cannot be editable while hidden — keep the pair coherent.
    if (!next.visible) next.editable = false;
    // Switching an opt-in field's grant ON opens it fully (visible + editable), the same
    // standard a regular field starts from; the admin narrows it to read-only by unticking
    // Bearbeitbar afterwards. Switching it OFF (above) drops back to the no-grant default.
    if (patch.visible === true && !current.visible && isOptIn(entity, field)) next.editable = true;
    setFieldEdits((prev) => {
      const m = new Map(prev ?? []);
      m.set(fieldKeyOf(entity, field), next);
      return m;
    });
  };

  // A system role is readOnly (name/Funktionen locked), but its Sichtbarkeit/Feldrechte
  // deny sections ARE editable + persistable (Kap. 5.8), so EVERY system role still needs
  // a Speichern button. The save skips the Funktionen write for them (see `save` below).
  const canSaveDenies = readOnly && (canSeeVisibility || canSeeFields);

  const toggleKey = (key: string) => {
    if (readOnly) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.adminRoles() });
    void qc.invalidateQueries({ queryKey: qk.adminRolesCatalog() });
    if (role) {
      // The deny set is cached per role; without this invalidation a reopened wizard
      // renders the pre-save set (issue 22). The local state already resets on unmount.
      void qc.invalidateQueries({ queryKey: qk.adminRoleVisibilityDenies(role.id) });
      void qc.invalidateQueries({ queryKey: qk.adminRoleFieldPermissions(role.id) });
    }
  };

  const save = useMutation({
    mutationFn: async () => {
      const keys = [...selected];
      if (mode === 'create') {
        await apiClient.admin.createRole({
          name,
          description,
          kind,
          permission_keys: keys,
          ist_administrationsrolle: istAdmin,
        });
        return;
      }
      if (!role) return;
      // A system role is IMMUTABLE for name/description/Funktionen. The api 403s the
      // wildcard one but would ACCEPT `basic`, so the client must not send it: saving a
      // system role persists only its Sichtbarkeit/Feldrechte denies below (Kap. 5.8).
      if (!readOnly) {
        await apiClient.admin.updateRole(role.id, {
          name,
          description,
          ist_administrationsrolle: istAdmin,
        });
        await apiClient.admin.setRolePermissions(role.id, keys);
      }
      if (canSeeVisibility && canManageVisibility && denies != null) {
        await apiClient.admin.setRoleVisibilityDenies(role.id, [...denies]);
      }
      // WS1 §5.2: persist Feldrechte. Each field is sent with its current
      // (visible, editable); the api drops all-default rows (Minus-Basis). An untouched
      // opt-in field goes out as (false, false) = its own default = dropped = stays hidden;
      // only a toggled-on one goes out as the visible=true GRANT row.
      //
      // The payload is the UNION of the offered catalog and the role's ALREADY-STORED
      // rows, because PUT field-permissions REPLACES the whole set: the catalog stops
      // offering entries whose backing column does not exist yet (`wire_ready`), so a
      // catalog-only payload would silently DELETE a stored rule on such a field.
      if (canSeeFields && canManageVisibility && fieldEdits != null) {
        const payload: FieldPermissionDto[] = [];
        const sent = new Set<string>();
        const entries = [
          ...(fieldCatalog.data?.catalog ?? []),
          ...(roleFields.data?.field_permissions ?? []),
        ];
        for (const f of entries) {
          const key = fieldKeyOf(f.entity_type, f.field_key);
          if (sent.has(key)) continue;
          sent.add(key);
          const r = ruleFor(f.entity_type, f.field_key);
          payload.push({
            entity_type: f.entity_type,
            field_key: f.field_key,
            visible: r.visible,
            editable: r.editable,
          });
        }
        await apiClient.admin.setRoleFieldPermissions(role.id, payload);
      }
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const del = useMutation({
    mutationFn: () => apiClient.admin.deleteRole(role!.id),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const kindLabel = kind === 'gruppe' ? 'Berechtigungsgruppe' : 'Rolle';

  return (
    <Dialog
      open
      onClose={onClose}
      wide
      title={mode === 'create' ? `${kindLabel} erstellen` : role?.name ?? kindLabel}
      description={
        readOnly
          ? 'Systemrolle: nur Ansicht.'
          : kind === 'gruppe'
            ? 'Rechte additiv zur Rolle.'
            : undefined
      }
      footer={
        <div className="flex flex-1 items-center justify-between">
          <div>
            {mode === 'edit' && !readOnly && can('role.manage') && (
              <Button
                variant="ghost"
                className="text-status-danger hover:bg-status-danger-bg"
                disabled={del.isPending}
                onClick={() => del.mutate()}
              >
                Löschen
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            {(!readOnly || canSaveDenies) && (
              <Button disabled={save.isPending || !name.trim()} onClick={() => save.mutate()}>
                Speichern
              </Button>
            )}
          </div>
        </div>
      }
    >
      {error != null && <div className="mb-3 text-sm text-status-danger">{error}</div>}

      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
            <TextInput
              value={name}
              disabled={readOnly}
              onChange={(e) => setName(e.target.value)}
              placeholder={kindLabel}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Beschreibung
            </label>
            <TextInput
              value={description}
              disabled={readOnly}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>

        {/* Administrationsrolle toggle (WS1 §5.8) — pure parametrization, no customer data */}
        {!isWildcard && (
          <label className="flex items-start gap-3 rounded-md border border-border bg-secondary/40 px-3 py-2">
            <input
              type="checkbox"
              aria-label="Administrationsrolle"
              checked={istAdmin}
              disabled={readOnly}
              onChange={(e) => setIstAdmin(e.target.checked)}
              className="mt-0.5 size-4 shrink-0 rounded"
            />
            <span className="min-w-0">
              <span className="text-sm font-medium">Administrationsrolle</span>
              <span className="block text-xs text-muted-foreground">
                Nur Parametrierung (Rollen, Teams, Aufgaben-Gruppen, Blocklisten, Einstellungen).
                Kein Zugriff auf Kundendaten. Kundenzugriff braucht eine separate Kundenrolle (Kap.
                5.8).
              </span>
            </span>
          </label>
        )}

        {/* Count row + Schnellauswahl */}
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2">
          <span className="text-sm tabular-nums">
            {isWildcard ? (
              <span className="font-medium">Alle Rechte (Wildcard)</span>
            ) : (
              <>
                <span className="font-medium tabular-nums">{selected.size}</span> von{' '}
                <span className="tabular-nums">{total}</span> Rechten ausgewählt
              </>
            )}
          </span>
          {!readOnly && !isWildcard && (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set(allKeys))}>
                Alle
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                Keine
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set(standardKeys))}>
                Standard
              </Button>
            </div>
          )}
        </div>

        {/* Permission catalog */}
        <div className="space-y-3">
          {catalog.categories.map((cat) => (
            <div key={cat.key} className="rounded-xl border border-border">
              <div className="flex items-center justify-between border-b border-border px-3 py-2">
                <span className="text-sm font-semibold">{cat.label}</span>
                {!readOnly && !isWildcard && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          cat.permissions.forEach((p) => next.add(p.key));
                          return next;
                        })
                      }
                    >
                      Alle wählen
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          cat.permissions.forEach((p) => next.delete(p.key));
                          return next;
                        })
                      }
                    >
                      Abwählen
                    </Button>
                  </div>
                )}
              </div>
              <ul className="divide-y divide-border">
                {cat.permissions.map((p) => {
                  const checked = isWildcard || selected.has(p.key);
                  return (
                    <li key={p.key} className="flex items-start gap-3 px-3 py-2">
                      <input
                        type="checkbox"
                        aria-label={p.label}
                        checked={checked}
                        disabled={readOnly || isWildcard}
                        onChange={() => toggleKey(p.key)}
                        className="mt-1 size-4 shrink-0 rounded"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{p.label}</span>
                          <code className="rounded bg-muted px-1 font-mono text-[11px] text-muted-foreground">
                            {p.key}
                          </code>
                        </div>
                        <div className="text-xs text-muted-foreground">{p.description}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Sichtbarkeit (deny-set) — Bereich 3: Menüpunkt → Tab → Kachel, cards nested
            under their tab. A tab/card deny REMOVES it from the client-detail UI. */}
        {canSeeVisibility && (
          <div className="rounded-xl border border-border">
            <div className="border-b border-border px-3 py-2">
              <div className="text-sm font-semibold">Sichtbarkeit</div>
              <div className="text-xs text-muted-foreground">
                Ausgewählte Bereiche, Tabs und Karten werden für diese Rolle ausgeblendet. Eine
                ausgeblendete Tab/Karte verschwindet aus der Kundenansicht; das Ausblenden eines
                Tabs blendet auch dessen Karten aus.
              </div>
            </div>
            <div className="py-1">{renderVisNodes(null, 0)}</div>
          </div>
        )}

        {/* Feldrechte (WS1 §5.2) — the finer per-field sichtbar/bearbeitbar layer */}
        {canSeeFields && (
          <div className="rounded-xl border border-border">
            <div className="border-b border-border px-3 py-2">
              <div className="text-sm font-semibold">Feldrechte</div>
              <div className="text-xs text-muted-foreground">
                Einzelne kritische Felder für diese Rolle ausblenden oder schreibgeschützt setzen
                (Standard: sichtbar und bearbeitbar). Felder mit besonderen Personendaten sind
                standardmässig ausgeblendet und werden erst durch eine explizite Freigabe sichtbar.
              </div>
            </div>
            <ul className="divide-y divide-border">
              {(fieldCatalog.data?.catalog ?? []).map((f) => {
                const rule = ruleFor(f.entity_type, f.field_key);
                const optIn = f.opt_in === true;
                return (
                  <li
                    key={fieldKeyOf(f.entity_type, f.field_key)}
                    className="px-3 py-2"
                    data-opt-in={optIn ? 'true' : undefined}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{f.label}</span>
                      {optIn ? (
                        <Badge variant="danger">besondere Personendaten · standardmässig aus</Badge>
                      ) : (
                        f.sensitive && <Badge variant="warning">sensibel</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{f.description}</div>
                    {optIn && (
                      <div className="text-xs text-muted-foreground">
                        Ohne explizite Freigabe für niemanden sichtbar, auch nicht für Administratoren.
                        «Sichtbar» anhaken = Freigabe für diese Rolle.
                      </div>
                    )}
                    <div className="mt-1 flex items-center gap-4">
                      <label className="flex items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          aria-label={`${f.label} sichtbar`}
                          checked={rule.visible}
                          disabled={!canManageVisibility}
                          onChange={(e) =>
                            patchFieldRule(f.entity_type, f.field_key, { visible: e.target.checked })
                          }
                          className="size-4 shrink-0 rounded"
                        />
                        Sichtbar
                      </label>
                      <label className="flex items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          aria-label={`${f.label} bearbeitbar`}
                          checked={rule.editable}
                          disabled={!canManageVisibility || !rule.visible}
                          onChange={(e) =>
                            patchFieldRule(f.entity_type, f.field_key, { editable: e.target.checked })
                          }
                          className="size-4 shrink-0 rounded"
                        />
                        Bearbeitbar
                      </label>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </Dialog>
  );
}

/** Adapter: CatalogRoleEntry → WizardRole (list rows use the catalog shape). */
export function toWizardRole(r: CatalogRoleEntry, description: string | null): WizardRole {
  return {
    id: r.id,
    name: r.name,
    description,
    is_system: r.is_system,
    is_wildcard: r.is_wildcard,
    ist_administrationsrolle: r.ist_administrationsrolle,
    selected_keys: r.selected_keys,
  };
}
